import React, { useMemo, useRef } from 'react';
import '@/utils/proj4.window.util';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/map';
import 'highcharts/modules/accessibility';
import worldMap from '@highcharts/map-collection/custom/world.geo.json';

// Simple debounce utility function
const debounce = <F extends (...args: any[]) => any>(func: F, wait: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function(this: any, ...args: Parameters<F>) {
    const later = () => {
      timeout = null;
      func.apply(this, args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
import { useDashboardData } from '@/services/dashboardDataService';
import { CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useWidgetStatusReporter } from '@/contexts/DashboardWidgetContext';
import './widgets.css';
import { cleanDateAndTimeObject } from '@shared/renderDate';

// Map region colors to match the legacy dashboard
const REGION_COLORS = {
  europe: '#3b82f6', // blue
  northAmerica: '#ef4444', // red
  southAmerica: '#f59e0b', // amber
  asia: '#8b5cf6', // violet
  middleEast: '#ec4899', // pink
  oceanPacific: '#10b981', // emerald
  sanctioned: '#ef4444', // red
  other: '#6b7280', // gray
} as const;

type RegionKey = keyof typeof REGION_COLORS;

// Define the data structure from our collector
interface StorageLocation {
  name: string;
  countryName: string;
  z: number;
}

type CategorizedData = {
  [K in RegionKey]: StorageLocation[];
};

interface CollectorResponse {
  data: {
    storageCounts: Record<string, number>;
    categorizedData: CategorizedData;
    lastUpdated: {
      toDate: () => Date;
    };
  };
  metadata?: {
    lastUpdated?: string;
  };
}

const DataStorageMapWidget: React.FC = () => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Fetch data from the dashboard data service
  const { data: response, isLoading, error } = useDashboardData('data-storage');

  // Extract the actual data from the nested response
  const collectorData = response;
  const hasData = collectorData && Object.keys(collectorData.storageCounts || {}).length > 0;

  // Report widget status to dashboard context
  useWidgetStatusReporter('data-storage-map', isLoading, error, hasData);

  // Debug log to see the actual data we're working with
  console.log('[DataStorageMapWidget] Collector Data:', JSON.stringify(response, null, 2));
  console.log('[DataStorageMapWidget] Has Data:', hasData);

  // Process data for the chart
  const chartOptions = useMemo<Highcharts.Options>(() => {
    const baseOptions: Highcharts.Options = {
      chart: {
        height: 280,
        width: 765,
        borderWidth: 0,
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      mapNavigation: { enabled: false },
    };

    if (!collectorData || !hasData) {
      return {
        ...baseOptions,
        series: [{
          type: 'map',
          name: 'Countries',
          mapData: worldMap as any,
          color: '#E0E0E0',
          enableMouseTracking: true,
          showInLegend: false,
        } as Highcharts.SeriesMapOptions]
      };
    }

    const { categorizedData } = collectorData;
    
    // Debug log to see the actual data we're working with
    console.log('[DataStorageMapWidget] Categorized Data:', JSON.stringify(categorizedData, null, 2));
    
    // Log the world map features to verify country names
    const countryNames = (worldMap as any).features.map((f: any) => f.properties.name);
    console.log('[DataStorageMapWidget] Available country names in map:', countryNames);

    const series: Array<Highcharts.SeriesMapOptions | Highcharts.SeriesMapbubbleOptions> = [
      // Base map layer
      {
        type: 'map',
        name: 'Countries',
        mapData: worldMap as any,
        color: '#E0E0E0',
        enableMouseTracking: true,
        showInLegend: false,
      } as Highcharts.SeriesMapOptions,
      // Region data layers
      ...(Object.entries(REGION_COLORS) as [RegionKey, string][]).map(
        ([region, color]) => {
          const regionData = (categorizedData?.[region as keyof CategorizedData] as StorageLocation[]) || [];
          console.log(`[DataStorageMapWidget] Region ${region} data:`, regionData);
          
          // Transform the data to match what Highcharts expects
          const mappedData = regionData.map(item => ({
            name: item.name,
            countryName: item.countryName,
            z: item.z,
            // Add coordinates if available, or let Highcharts look them up by name
            lat: item['lat'],
            lon: item['lon']
          }));
          
          return {
            type: 'mapbubble',
            color,
            name: region
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())
              .replace('America', 'America '),
            mapData: worldMap as any,
            joinBy: ['name', 'countryName'],
            data: mappedData,
            minSize: 10,
            maxSize: '10%',
            cursor: 'pointer',
            states: {
              hover: {
                halo: {
                  size: 5,
                  opacity: 0.5,
                },
              },
            },
            dataLabels: {
              enabled: false,
            },
          } as Highcharts.SeriesMapbubbleOptions;
        }
      )
    ];
    
    console.log('Final series configuration:', JSON.stringify(series, null, 2));

    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        map: worldMap as any, // Make sure the chart has the map data
      },
      legend: {
        enabled: false
      },
      plotOptions: {
        map: {
          states: {
            hover: {
              color: '#BADA55'
            }
          }
        },
        mapbubble: {
          minSize: 10,
          maxSize: '10%',
          dataLabels: {
            enabled: true,
            format: '{point.name}'
          }
        }
      },
      tooltip: {
        headerFormat: '<b>{point.name}</b><br>',
        pointFormat: 'Assets: <b>{point.z}</b>',
      },
      series
    };
  }, [collectorData, hasData]);

  // Show loading state
  if (isLoading) {
    return (
      <CardContent className="h-full flex flex-col p-4" style={{ containerType: 'inline-size' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Data Storage Locations</h3>
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-full w-full" />
        </div>
      </CardContent>
    );
  }

  // Show error state
  if (error) {
    return (
      <CardContent className="h-full p-4" style={{ containerType: 'inline-size' }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Data Storage Locations</h3>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Data</AlertTitle>
          <AlertDescription>
            {error || 'Failed to load data storage information.'}
          </AlertDescription>
        </Alert>
      </CardContent>
    );
  }

  const lastUpdated = cleanDateAndTimeObject(collectorData?.lastUpdated);

  // Use a wider aspect ratio that better fits world maps (approximately 2:1)
  const aspectRatio = 2;
  
  return (
    <CardContent className="h-full flex flex-col p-0" style={{ containerType: 'inline-size' }}>
      <div className="flex justify-between items-center p-4 pb-0">
        <h3 className="text-lg font-semibold">Data Storage Locations</h3>
        {lastUpdated && (
          <span className="text-xs text-muted-foreground">
            Updated: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>
      <div className="flex-1 relative" style={{ minHeight: '300px' }}>
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden'
          }}
        >
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={{
              ...chartOptions,
              chart: {
                ...chartOptions.chart,
                width: null,
                height: null,
                events: {
                  load: function() {
                    const chart = this;
                    let resizeObserver: ResizeObserver | null = null;
                    
                    // Only proceed if chart and its container exist
                    if (!chart || !chart.container || !chart.container.parentElement) {
                      return;
                    }
                    
                    const container = chart.container.parentElement.parentElement;
                    if (!container) return;
                    
                    const handleResize = () => {
                      try {
                        // Check if chart is still valid
                        if (!chart || !chart.container) {
                          if (resizeObserver) {
                            resizeObserver.disconnect();
                            resizeObserver = null;
                          }
                          return;
                        }
                        
                        const containerWidth = container.clientWidth;
                        const containerHeight = container.clientHeight;
                        
                        // Skip if container has no size
                        if (containerWidth <= 0 || containerHeight <= 0) {
                          return;
                        }
                        
                        // Calculate the available space ratio
                        const containerRatio = containerWidth / containerHeight;
                        let chartWidth, chartHeight;
                        
                        if (containerRatio > aspectRatio) {
                          // Container is wider than map's aspect ratio - limit by height
                          chartHeight = Math.max(containerHeight, 100);
                          chartWidth = chartHeight * aspectRatio;
                        } else {
                          // Container is taller than map's aspect ratio - limit by width
                          chartWidth = Math.max(containerWidth, 100);
                          chartHeight = chartWidth / aspectRatio;
                        }
                        
                        // Ensure the chart doesn't exceed container dimensions
                        if (chartWidth > containerWidth) {
                          chartWidth = containerWidth;
                          chartHeight = chartWidth / aspectRatio;
                        }
                        
                        if (chartHeight > containerHeight) {
                          chartHeight = containerHeight;
                          chartWidth = chartHeight * aspectRatio;
                        }
                        
                        // Set the chart size with animation disabled
                        if (chart && chart.setSize) {
                          chart.setSize(Math.floor(chartWidth), Math.floor(chartHeight), false);
                        }
                      } catch (error) {
                        console.error('Error in chart resize handler:', error);
                        if (resizeObserver) {
                          resizeObserver.disconnect();
                          resizeObserver = null;
                        }
                      }
                    };
                    
                    // Add debounce to prevent excessive updates
                    const debouncedResize = debounce(handleResize, 100);
                    
                    // Initial resize with a small delay to ensure container is ready
                    const initTimer = setTimeout(() => {
                      if (chart) {
                        handleResize();
                      }
                    }, 100);
                    
                    // Set up resize observer
                    resizeObserver = new ResizeObserver(debouncedResize);
                    resizeObserver.observe(container);
                    
                    // Cleanup function
                    return () => {
                      clearTimeout(initTimer);
                      if (resizeObserver) {
                        resizeObserver.disconnect();
                      }
                      window.removeEventListener('resize', debouncedResize);
                    };
                  }
                }
              }
            }}
            constructorType={'mapChart'}
            immutable={true}
          />
        </div>
      </div>
    </CardContent>
  );
};

export default DataStorageMapWidget;
